/**
 * JointJS 그래프 → CCTV 관계 도출 입력.
 *
 * 좌표는 관계선을 그리는 공간과 같아야 하므로 element.getBBox().center() 를 쓴다
 * (zone-auto-generate/lib/graphAdapter 와 같은 이유 — cctv-ip-import 의
 * elementPoint 는 도면 미터 좌표라 여기엔 맞지 않는다).
 */

import type { dia } from '@joint/core'

import { isCctvElement } from '../../editor/lib/zoneCctvRelations'
import { routeLayerToCategory } from '../../../map-editor-shared/lib/layerUtils'
import { minAreaRect } from '../../../map-editor-pages/editor/lib/minAreaRect'
import type { RelationCamera, RelationObstacle } from '../model/types'

function cellProps(element: dia.Element): Record<string, unknown> {
  const data = (element.get('data') as { properties?: Record<string, unknown> }) || {}
  return data.properties || {}
}

/** DB 에 저장된 이름과 같아야 관계가 맞물린다 — properties.name 이 유일한 출처. */
export function cctvName(element: dia.Element): string {
  const name = cellProps(element).name
  return typeof name === 'string' ? name.trim() : ''
}

/** 현재 층 그래프의 CCTV 들. 이름이 없는 카메라는 관계를 맺을 수 없어 뺀다. */
export function collectRelationCameras(graph: dia.Graph | null): RelationCamera[] {
  if (!graph) return []
  const cams: RelationCamera[] = []
  for (const element of graph.getElements()) {
    if (!isCctvElement(element)) continue
    const name = cctvName(element)
    if (!name) continue
    const center = element.getBBox().center()
    cams.push({ name, x: center.x, y: center.y })
  }
  return cams
}

/** 이름 → 셀. 관계선을 그릴 때 양 끝 셀을 찾는 데 쓴다. */
export function cctvElementsByName(graph: dia.Graph | null): Map<string, dia.Element> {
  const map = new Map<string, dia.Element>()
  if (!graph) return map
  for (const element of graph.getElements()) {
    if (!isCctvElement(element)) continue
    const name = cctvName(element)
    // 같은 이름이 두 번 찍힌 맵이 있다 — 먼저 만난 셀을 쓴다(도출부와 같은 규칙).
    if (name && !map.has(name)) map.set(name, element)
  }
  return map
}

/**
 * 현재 층의 주차면 외곽(회전 반영 4귀퉁이). 자동 생성이 이 폴리곤을 관통하는
 * 연결을 버린다. 판별 기준은 parkingScaleMetrics 와 동일(layer →
 * parkingLocation 카테고리).
 */
export function collectRelationObstacles(graph: dia.Graph | null): RelationObstacle[] {
  if (!graph) return []
  const out: RelationObstacle[] = []
  for (const element of graph.getElements()) {
    const layer = (element.get('data') as { layer?: unknown })?.layer
    if (typeof layer !== 'string' || routeLayerToCategory(layer) !== 'parkingLocation') continue
    // CSV 렌더는 대각선 주차면도 엘리먼트 bbox 는 축 정렬로 두고 path 만
    // 기울여 그린다 — bbox 를 그대로 쓰면 부풀어 통로까지 막는다. 실제
    // 꼭짓점(data.vertices)이 있으면 그 최소 회전 사각형을 쓴다.
    const vertices = (element.get('data') as { vertices?: [number, number][] })?.vertices
    if (Array.isArray(vertices) && vertices.length >= 3) {
      out.push(minAreaRect(vertices.map(([vx, vy]) => ({ x: vx, y: vy }))))
      continue
    }
    const { x, y } = element.position()
    const { width, height } = element.size()
    if (width <= 0 || height <= 0) continue
    const angle = ((element.angle() || 0) * Math.PI) / 180
    const cx = x + width / 2
    const cy = y + height / 2
    const cos = Math.cos(angle)
    const sin = Math.sin(angle)
    out.push(
      (
        [
          [x, y],
          [x + width, y],
          [x + width, y + height],
          [x, y + height],
        ] as [number, number][]
      ).map(([px, py]) => ({
        x: cx + (px - cx) * cos - (py - cy) * sin,
        y: cy + (px - cx) * sin + (py - cy) * cos,
      })),
    )
  }
  return out
}
